/**
 * Client-side base-construction mode: placement intent + ghost preview.
 *
 * This system never authors truth — it only computes where the player is aiming,
 * snaps that point to the shared build grid, runs the *same* deterministic
 * terrain/occupancy rules as `server/src/systems/Structures.ts` for a local
 * preview, and hands a `BuildPlacementIntent` to the host. The host (Game.ts)
 * sends that intent to the server as `place_structure`; the authoritative room
 * validates it again and publishes the result through `structures` state.
 *
 * Aim is a ray from the camera through a pointer (screen centre by default).
 * The ray is intersected with the `y = 0` water plane to recover a world (x,z);
 * the ghost is then locked to `islandFloor(x,z)` so it sits on the island.
 */
import * as THREE from 'three';
import { ISLAND_FIELD, islandFloor, islandSlope } from '../../shared/islandField';

export type BuildStructureType = 'crate' | 'barrel' | 'chest' | 'cannon';

export interface BuildPlacementIntent {
  type: BuildStructureType;
  x: number;
  y: number;
  z: number;
  yaw: number;
}

/** A placed structure's footprint, used for the local overlap preview. */
export interface BuildOccupant {
  x: number;
  z: number;
  halfX: number;
  halfZ: number;
}

export interface BuildModeOptions {
  /** Invoked with the confirmed intent; the host sends it to the server. */
  onPlace?: (intent: BuildPlacementIntent) => void;
  /** Initial structure type. */
  type?: BuildStructureType;
  /** Build distance limit in metres (mirrors the server default). */
  maxDistance?: number;
}

const BUILD_GRID = ISLAND_FIELD.buildGrid;
const BUILD_MIN_HEIGHT = 0.5;
const DEFAULT_MAX_DISTANCE = 300;
const YAW_SNAP = Math.PI / 4;

/** Footprint + slope tuning mirrored from `Structures.STRUCTURE_DEFS`. */
const GHOST_HALF_EXTENTS: Readonly<Record<BuildStructureType, { halfX: number; halfZ: number }>> = {
  crate: { halfX: 1.0, halfZ: 1.0 },
  barrel: { halfX: 0.75, halfZ: 0.75 },
  chest: { halfX: 1.1, halfZ: 1.1 },
  cannon: { halfX: 1.4, halfZ: 0.9 },
};

const GHOST_HEIGHTS: Readonly<Record<BuildStructureType, number>> = {
  crate: 1.2,
  barrel: 1.1,
  chest: 0.9,
  cannon: 1.0,
};

const GHOST_MAX_SLOPE: Readonly<Record<BuildStructureType, number>> = {
  crate: 0.5,
  barrel: 0.5,
  chest: 0.5,
  cannon: 0.35,
};

const VALID_COLOR = 0x2fff9a;
const INVALID_COLOR = 0xff4d5a;

function snapGrid(value: number): number {
  return Math.round(value / BUILD_GRID) * BUILD_GRID;
}

function wrapAngle(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

/**
 * Placement intent + translucent ghost preview. The host toggles it with
 * `setEnabled`, feeds the camera/ship/occupants each frame via `update`, and
 * confirms with `confirm()` on the build key/click.
 */
export class BuildMode {
  enabled = false;
  type: BuildStructureType;

  private readonly onPlace: ((intent: BuildPlacementIntent) => void) | undefined;
  private readonly maxDistance: number;
  private readonly group = new THREE.Group();
  private readonly ghost = new THREE.Group();
  private readonly ghostMesh: THREE.Mesh;
  private readonly ghostRing: THREE.Mesh;
  private readonly ghostMaterial: THREE.MeshBasicMaterial;
  private readonly ringMaterial: THREE.MeshBasicMaterial;
  private readonly raycaster = new THREE.Raycaster();
  private yaw = 0;
  private currentIntent: BuildPlacementIntent | null = null;
  private currentValid = false;

  constructor(scene: THREE.Scene, options: BuildModeOptions = {}) {
    this.onPlace = options.onPlace;
    this.type = options.type ?? 'crate';
    this.maxDistance = Math.max(1, options.maxDistance ?? DEFAULT_MAX_DISTANCE);

    this.group.name = 'build-mode';
    this.group.visible = false;
    scene.add(this.group);

    this.ghostMaterial = new THREE.MeshBasicMaterial({
      color: VALID_COLOR,
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
    });
    this.ringMaterial = new THREE.MeshBasicMaterial({
      color: VALID_COLOR,
      transparent: true,
      opacity: 0.8,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    this.ghostMesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), this.ghostMaterial);
    this.ghostRing = new THREE.Mesh(new THREE.RingGeometry(0.8, 1, 40), this.ringMaterial);
    this.ghostRing.rotation.x = -Math.PI / 2;
    this.ghostRing.position.y = 0.03;

    this.ghost.add(this.ghostMesh, this.ghostRing);
    this.group.add(this.ghost);
    this.applyType();
    this.applyYaw();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.group.visible = false;
      this.currentIntent = null;
      this.currentValid = false;
    }
  }

  /**
   * Show/hide the debug build-tour how-to panel (`#build-tour-panel`). The
   * panel is static DOM in `index.html`; BuildMode owns its visibility because
   * the instructions are about building, and the tour hides it on return.
   */
  setTourActive(active: boolean): void {
    if (typeof document === 'undefined') return;
    const panel = document.getElementById('build-tour-panel');
    if (panel) panel.hidden = !active;
  }

  setType(type: BuildStructureType): void {
    this.type = type;
    this.applyType();
  }

  setYaw(yaw: number): void {
    this.yaw = wrapAngle(yaw);
    this.applyYaw();
  }

  /** Rotate the placement yaw by one or more 45° steps. */
  rotateYaw(steps = 1): void {
    this.setYaw(this.yaw + steps * YAW_SNAP);
  }

  /**
   * Recompute the ghost at the aim point. Returns the current intent when it is
   * valid, otherwise null. `pointerNdc` is in normalised device coordinates
   * (-1..1); the default is the screen centre.
   */
  update(
    camera: THREE.Camera,
    shipX: number,
    shipZ: number,
    occupants: readonly BuildOccupant[] = [],
    pointerNdc: { x: number; y: number } = { x: 0, y: 0 },
  ): BuildPlacementIntent | null {
    if (!this.enabled) {
      this.group.visible = false;
      this.currentIntent = null;
      this.currentValid = false;
      return null;
    }

    this.raycaster.setFromCamera(new THREE.Vector2(pointerNdc.x, pointerNdc.y), camera);
    const { origin, direction } = this.raycaster.ray;

    // Intersect the aim ray with the y=0 water plane to get a world (x,z).
    if (Math.abs(direction.y) < 1e-6) {
      this.hideGhost();
      return null;
    }
    const t = -origin.y / direction.y;
    if (!Number.isFinite(t) || t <= 0) {
      this.hideGhost();
      return null;
    }

    const x = snapGrid(origin.x + direction.x * t);
    const z = snapGrid(origin.z + direction.z * t);
    const ground = islandFloor(x, z);
    const groundSlope = islandSlope(x, z);
    const extents = GHOST_HALF_EXTENTS[this.type];

    const valid =
      Number.isFinite(ground) &&
      Number.isFinite(groundSlope) &&
      ground > BUILD_MIN_HEIGHT &&
      groundSlope <= GHOST_MAX_SLOPE[this.type] &&
      Math.hypot(x - shipX, z - shipZ) <= this.maxDistance &&
      !this.overlaps(x, z, extents.halfX, extents.halfZ, occupants);

    this.currentValid = valid;
    this.currentIntent = { type: this.type, x, y: ground, z, yaw: this.yaw };

    this.ghost.position.set(x, ground, z);
    this.ghost.visible = true;
    this.group.visible = true;
    this.setGhostColor(valid);
    return valid ? this.currentIntent : null;
  }

  /** Confirm the current valid intent (and notify the host), or return null. */
  confirm(): BuildPlacementIntent | null {
    if (!this.enabled || !this.currentValid || !this.currentIntent) return null;
    const intent = this.currentIntent;
    this.onPlace?.(intent);
    return intent;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.ghostMesh.geometry.dispose();
    this.ghostRing.geometry.dispose();
    this.ghostMaterial.dispose();
    this.ringMaterial.dispose();
  }

  private applyType(): void {
    const extents = GHOST_HALF_EXTENTS[this.type];
    const height = GHOST_HEIGHTS[this.type];
    this.ghostMesh.scale.set(extents.halfX * 2, height, extents.halfZ * 2);
    this.ghostMesh.position.y = height / 2;
    this.ghostRing.scale.set(extents.halfX + 0.3, 1, extents.halfZ + 0.3);
  }

  private applyYaw(): void {
    this.ghost.rotation.y = this.yaw;
  }

  private setGhostColor(valid: boolean): void {
    const color = valid ? VALID_COLOR : INVALID_COLOR;
    this.ghostMaterial.color.setHex(color);
    this.ringMaterial.color.setHex(color);
  }

  private hideGhost(): void {
    this.group.visible = false;
    this.currentIntent = null;
    this.currentValid = false;
  }

  private overlaps(
    x: number,
    z: number,
    halfX: number,
    halfZ: number,
    occupants: readonly BuildOccupant[],
  ): boolean {
    for (const occupant of occupants) {
      if (
        Math.abs(occupant.x - x) <= occupant.halfX + halfX &&
        Math.abs(occupant.z - z) <= occupant.halfZ + halfZ
      ) {
        return true;
      }
    }
    return false;
  }
}
